import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { getAuthUrl } from "@/utils/redirectUtils";

interface Feature {
  icon: ReactNode;
  title: string;
  description: string;
}

interface TierPageLayoutProps {
  tierName: string;
  tagline: string;
  headline: string;
  subheadline: string;
  price: string | number;
  priceNote?: string;
  bestFor: string;
  audienceDescription: string;
  features: Feature[];
  limits: string[];
  useCases: { title: string; description: string }[];
  ctaText: string;
  isEnterprise?: boolean;
}

const TierPageLayout = ({
  tierName,
  tagline,
  headline,
  subheadline,
  price,
  priceNote,
  bestFor,
  audienceDescription,
  features,
  limits,
  useCases,
  ctaText,
  isEnterprise = false,
}: TierPageLayoutProps) => {
  const handleCTA = () => {
    if (isEnterprise) {
      window.open("mailto:george@upfixe.com?subject=Enterprise Plan Inquiry", "_blank");
    } else {
      window.location.href = getAuthUrl();
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow pt-24">
        {/* Hero Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-primary/5 to-primary/10">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center">
              <Badge className="mb-6 text-sm font-medium bg-primary text-primary-foreground">
                {tagline}
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-foreground">
                {headline}
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-4xl mx-auto">
                {subheadline}
              </p>
              
              <div className="flex flex-col items-center gap-4 mb-8">
                <div className="text-5xl font-bold text-foreground">
                  {typeof price === "number" ? `$${price}/mo` : price}
                </div>
                {priceNote && (
                  <p className="text-muted-foreground">{priceNote}</p>
                )}
              </div>
              
              <Button 
                size="lg" 
                className="text-lg px-8 h-14 font-bold"
                onClick={handleCTA}
              >
                {ctaText} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              
              <p className="text-muted-foreground text-sm mt-4">
                {isEnterprise ? "Custom solutions for your organization" : "No credit card required"}
              </p>
            </div>
          </div>
        </section>

        {/* Best For Section */}
        <section className="py-16 px-4 bg-background">
          <div className="container mx-auto max-w-4xl text-center">
            <Badge variant="secondary" className="mb-4">Best For</Badge>
            <h2 className="text-3xl font-bold mb-4">{bestFor}</h2>
            <p className="text-xl text-muted-foreground">{audienceDescription}</p>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">What's Included</h2>
              <p className="text-xl text-muted-foreground">Everything you get with {tierName}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {limits.map((limit, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-background rounded-lg border">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-lg font-medium">{limit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 px-4 bg-background">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Key Features</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="border-border">
                  <CardContent className="p-6">
                    <div className="bg-primary/10 rounded-full p-3 w-12 h-12 flex items-center justify-center mb-4 text-primary">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Perfect For</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => (
                <div key={index} className="p-6 bg-background rounded-lg border">
                  <h3 className="text-xl font-semibold mb-2">{useCase.title}</h3>
                  <p className="text-muted-foreground">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-4 bg-primary text-primary-foreground">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-8">
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-12 opacity-90">
              Create better Google Ads campaigns with Upfixe Ads.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary"
                className="text-lg px-8 h-14 font-bold"
                onClick={handleCTA}
              >
                {ctaText} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-lg px-8 h-14 border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 font-bold"
                asChild
              >
                <Link to="/pricing">Compare All Plans</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TierPageLayout;